import type { ParsedUrlQuery } from 'querystring';
import { INavItem } from '@creditflowresearch/uilib/DropdownPanelButtons';
import { AbsProduct, IgcProduct, HycProduct } from '@/creditflowresearch/domain/Product';
import { getProductColor } from '@/creditflowresearch/ui/common/utils/products/productStyleHelpers';
import ProductBadge from '@/creditflowresearch/ui/common/components/atoms/ProductBadge';
import type { ProductAbbreviations } from '@/creditflowresearch/domain/Product';
import EmergingMarket from '@/creditflowresearch/domain/Market/entities/EmergingMarket';
import URL from '@/creditflowresearch/Router/urlConstants';
import Region from '@/creditflowresearch/domain/Region';
import { MarketSegment } from '@/creditflowresearch/domain/Market';
import type { ILinkPanelButton } from '@creditflowresearch/uilib/LinkPanelButton';
import { getMarketOverviewUrl } from '@/creditflowresearch/domain/Product/helpers/getMarketOverviewUrl';
import { hasEmMarket } from '@/creditflowresearch/domain/Market/selectors/hasEmMarket';
import type { IEmNavParams } from '@/creditflowresearch/ui/common/hooks/panels/useEmNavParams';

export interface IPanelHeaderConfig {
  title: string,
}

export interface INavItemData {
  id: string,
  abbreviation: ProductAbbreviations,
  label: string,
  header: IPanelHeaderConfig,
  sectionsTitle: string,
}

export const NAV_ITEMS_TREE: INavItemData[] = [
  {
    id: AbsProduct.PRODUCT_ID,
    abbreviation: AbsProduct.ABBREVIATION,
    label: 'Asset Backed Securities',
    header: {
      title: 'Asset Backed Securities',
    },
    sectionsTitle: 'sector/subsector profiles',
  },
  {
    id: HycProduct.PRODUCT_ID,
    abbreviation: HycProduct.ABBREVIATION,
    label: 'High Yield',
    header: {
      title: 'High Yield',
    },
    sectionsTitle: 'industry/subindustry profiles',
  },
  {
    id: IgcProduct.PRODUCT_ID,
    abbreviation: IgcProduct.ABBREVIATION,
    label: 'Investment Grade',
    header: {
      title: 'Investment Grade',
    },
    sectionsTitle: 'industry/subindustry profiles',
  },
];

const DEFAULT_EM_REGIONS = [
  Region.APAC_ABBREVIATION,
  Region.EMEA_ABBREVIATION,
  Region.LATM_ABBREVIATION,
].toString();

const DEFAULT_EM_SEGMENTS = [MarketSegment.CORP, MarketSegment.FIG, MarketSegment.SSA].toString();

export const getNavLinks = (query: ParsedUrlQuery, emNav: IEmNavParams = {}): ILinkPanelButton[] => ([
  {
    abbreviation: EmergingMarket.ABBREVIATION,
    label: 'Emerging Markets',
    isActive: hasEmMarket(query),
    link: {
      pathname: URL.MARKET_OVERVIEW_EM,
      query: {
        regions: emNav.regions || DEFAULT_EM_REGIONS,
        segments: emNav.segments || DEFAULT_EM_SEGMENTS,
        markets: EmergingMarket.ABBREVIATION,
      },
    },
  },
]);

export const NAV_ITEMS: INavItem[] = NAV_ITEMS_TREE.map(({ id, label, abbreviation }) => ({
  id,
  label,
  abbreviation,
  href: getMarketOverviewUrl(id) ?? undefined,
  badge: () => (
    <ProductBadge
      text={ abbreviation }
      productColor={ getProductColor(abbreviation) }
      productId={ id }
    />
  ),
}));

export const getPanelData = (panelId: string): INavItemData | null => {
  return NAV_ITEMS_TREE.find((item) => item.id === panelId) || null;
};
